import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { DateTime } from 'luxon';
import { ListHelperService, ProfileService, StoreService } from 'core/services';
import { LicenseSessionsService } from '../services/license-sessions.service';
import { License } from 'core/models';

@Injectable()
export class LicensesLogService {
  private userRole: any;
  private userLang: string;
  private license: License;

  constructor(
    private translate: TranslateService,
    private profileService: ProfileService,
    private listHelper: ListHelperService,
    private licenseSessionsService: LicenseSessionsService,
    private activatedRoute: ActivatedRoute
  ) {
    this.activatedRoute.data.subscribe((data) => {
      this.license = data['license'];
    });
    this.profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
    this.userLang = this.profileService.getLang();
  }

  async getDatasource(listId, options: any = {}, license) {
    const dataSource = new TableDatasource({
      columns: this.getColumns(license),
      load: (props) => this.fetch(props, dataSource),
      storeState: {
        enabled: true,
        key: `${listId}_${this.userRole.name}`
      },
      ...options
    });
    await dataSource.refreshData();
    StoreService.create(listId, dataSource);
    return dataSource;
  }

  fetch({ page, limit, sort, search, filters, aggregates, populate }, dataSource: TableDatasource) {
    const { licenseId, date, ...$filterValues } = filters;
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource, this.getCustomFilters());

    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      aggregates,
      populate,
      licenseId,
      date,
      $filter
    };
    return this.licenseSessionsService.fetch(paginationOptions).then((response) => response);
  }

  getColumns(license) {
    const { isSuperlicense } = license;
    const columns = [
      {
        source: 'userEmail',
        style: { width: '150px' },
        label: this.translate.instant('nouns.email'),
        allowFiltering: false,
        sortable: true
      },
      {
        source: 'gameName',
        style: { width: '150px' },
        label: this.translate.instant('licenses.header.gameName'),
        allowFiltering: false,
        valueParser: () => {
          return this.license.courseTitle[this.userLang] ?? this.license.courseTitle['en'] ?? this.license.gameName;
        }
      },

      {
        source: 'startDate',
        style: { width: '140px' },
        label: this.translate.instant('reports.firstConnection'),
        sortable: true,
        allowFiltering: false,
        dataType: DataTypes.datetime,
        valueParser: (value, license) => {
          const format = DateTime.DATETIME_MED_WITH_SECONDS;
          return this.listHelper.parseDate(value, license.timezone, format);
        }
      },
      {
        source: 'endDate',
        style: { width: '140px' },
        label: this.translate.instant('reports.lastConnection'),
        sortable: false,
        allowFiltering: false,
        valueParser: (value, license) => {
          const format = DateTime.DATETIME_MED_WITH_SECONDS;
          return this.listHelper.parseDate(value, license.timezone, format);
        }
      },
      {
        source: 'values.points',
        style: { width: '140px' },
        label: isSuperlicense
          ? this.translate.instant('reports.modulePoints')
          : this.translate.instant('reports.points'),
        allowFiltering: false,
        sortable: true,
        valueParser: (value) => {
          return value == null ? '' : '' + value;
        }
      },
      {
        source: 'values.turn',
        style: { width: '80px' },
        label: this.translate.instant('reports.level'),
        allowFiltering: false,
        sortable: true,
        valueParser: (value) => {
          return value == null ? '' : '' + (value + 1);
        }
      },
      {
        source: 'durationSeconds',
        style: { width: '80px' },
        label: this.translate.instant('reports.duration'),
        sortable: true,
        allowFiltering: false,
        valueParser: (value, license) => this.listHelper.parsePlaytime(value)
      },
      {
        source: 'values.gameProgress',
        style: { width: '80px' },
        label: this.translate.instant('reports.gameProgress'),
        allowFiltering: false,
        sortable: true,
        valueParser: (value, license) => {
          const progress = license.values?.gameProgress ? Math.round(license.values.gameProgress) : 0;
          return progress + '%';
        }
      }
    ];
    return columns.filter((col) => col !== null);
  }

  getCustomFilters() {
    return {};
  }
}
